import React from 'react'
import { OnClickMenu, OnClickMenuItem } from 'uiComponents/menus'
import { LucideIcon } from 'utils/lucideIcon'
import './discountsHeader.scss'
import SearchBar from 'uiComponents/search/searchBar'
import { NavigationTabs } from 'uiComponents/navigation/tabs'
import { useLocation, useParams } from 'react-router-dom'
import { BaseRouteParams } from 'hocs'
import { history } from 'middleware'
import { isLocalStorageAvailable } from 'utils/storage'
import { useTranslation } from 'translations'

interface DiscountsHeaderProps {
    onClickNewDiscount: () => void
    onClickNewDiscountPool: () => void
}

export const DiscountsHeader = ({ onClickNewDiscount, onClickNewDiscountPool }: DiscountsHeaderProps) => {
    const { t } = useTranslation()
    const location = useLocation()
    const activeTab = location.pathname.includes('/pools') ? 'pools' : 'codes'
    const searchByOptions = [
        { value: 'code', name: t('discounts.discountsHeader.searchBy.code') },
        { value: 'discount_reservoir_name', name: t('discounts.discountsHeader.searchBy.poolName') },
        { value: 'product_id', name: t('discounts.discountsHeader.searchBy.productId') },
        { value: 'order_number', name: t('discounts.discountsHeader.searchBy.orderNumber') },
        { value: 'validity_date', name: t('discounts.discountsHeader.searchBy.bookingWindow'), inputType: 'date' },
    ]
    const routeParams = useParams<BaseRouteParams>()
    const basePath = `/account/${routeParams.accountSlug}/discounts`

    let defaultSearchBy = 'discount_reservoir_name'
    let autoSearchByPatterns
    if (activeTab === 'codes') {
        defaultSearchBy = 'code'
        if (isLocalStorageAvailable()) {
            defaultSearchBy = localStorage.getItem('discounts_search_by') ?? defaultSearchBy
        }
        autoSearchByPatterns = [
            [/^\d{8,9}$/, 'order_number'],
            [/^\d{4}$/, 'product_id'],
        ] as [RegExp, string][]
    }

    const onSearchByChange = (searchBy: string) => {
        if (searchBy === 'discount_reservoir_name') {
            history.push(`${basePath}/pools`)
        } else {
            if (isLocalStorageAvailable()) {
                localStorage.setItem('discounts_search_by', searchBy)
            }
            history.push(`${basePath}/codes`)
        }
    }

    return (
        <div className="discount-header-container">
            <div className="column">
                <SearchBar
                    searchByOptions={searchByOptions}
                    defaultSearchBy={defaultSearchBy}
                    autoSearchByPatterns={autoSearchByPatterns}
                    onSearchByChange={onSearchByChange}
                    placeholder={t('discounts.discountsHeader.searchPlaceholder')}
                    showClearButton
                />
            </div>
            <div className="column">
                <NavigationTabs
                    tabs={[
                        {
                            current: activeTab === 'pools',
                            name: t('discounts.discountsHeader.tabs.pools'),
                            onClick: () => {
                                history.push(`${basePath}/pools`)
                            },
                        },
                        {
                            current: activeTab === 'codes',
                            name: t('discounts.discountsHeader.tabs.codes'),
                            onClick: () => {
                                history.push(`${basePath}/codes`)
                            },
                        },
                    ]}
                />
            </div>
            <div className="column">
                <OnClickMenu title={t('common.create')} className="create" size="large">
                    <OnClickMenuItem onClick={onClickNewDiscount}>
                        <LucideIcon iconName="Tag" />
                        {t('discounts.discountsHeader.newDiscount')}
                    </OnClickMenuItem>
                    <OnClickMenuItem onClick={onClickNewDiscountPool}>
                        <LucideIcon iconName="Tags" />
                        {t('discounts.discountsHeader.newDiscountPool')}
                    </OnClickMenuItem>
                </OnClickMenu>
            </div>
        </div>
    )
}
